<template>
  <panel
    :breadcrumbs="breadcrumbs"
    :title="title"
  >
    <v-card>
      <v-alert
        type="info"
        :value="true"
        class="mb-4"
        outline
      >
        <strong>
          Do take note :
          <br>
          • The display reflects the payout month.
          <v-spacer />
          • The incentive is calculated using the registered number at the end of each month.
        </strong>
      </v-alert>

      <v-toolbar
        dense
        card
      >
        <v-menu
          v-model="menu"
          lazy
          close-on-content-click
          transition="scale-transition"
          full-width
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="monthOf"
            prepend-icon="event"
            readonly
          />

          <v-date-picker
            v-model="monthOf"
            :max="maxDate"
            :allowed-dates="allowedMonths"
            min="2025-08"
            type="month"
            year-icon="date_range"
          />
        </v-menu>

        <v-spacer />
      </v-toolbar>

      <v-card-title>
        <v-spacer />

        <v-text-field
          v-model="search"
          append-icon="search"
          label="Search"
          single-line
          solo-inverted
          clearable
          box
          hide-details
        />
      </v-card-title>

      <!-- 1st MNP Incentive -->
      <v-card-title>
        <h3 class="primary--text">
          {{ firstMnpIncentiveLabel }}
        </h3>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :pagination.sync="pagination"
        :search="search"
        class="px-1 pb-1"
        item-key="name"
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <td class="text-xs-left">
            {{ props.item.entry }}
          </td>

          <td class="text-xs-left">
            {{ props.item.msisdn }}
          </td>

          <td class="text-xs-center">
            {{ props.item.category }}
          </td>

          <td class="text-xs-center">
            {{ props.item.amount }}
          </td>
        </template>

        <template slot="no-data">
          {{ noDataText }}
        </template>

        <template slot="no-result">
          <v-alert
            :value="true"
            color="error"
            icon="warning"
          >
            {{ noResultText }}
          </v-alert>
        </template>

        <v-spacer /><v-spacer />

        <template slot="footer">
          <tr>
            <td>&nbsp;</td>
            <td>&nbsp;</td>

            <td class="text-xs-right">
              Total 1st Incentive:
            </td>

            <td
              class="primary text-xs-center"
            >
              RM {{ totalPayDay1 }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-divider class="primary" />

      <!-- 2nd MNP Incentive -->
      <v-card-title>
        <h3 class="primary--text">
          {{ secondMnpIncentiveLabel }}
        </h3>

        <v-spacer />
      </v-card-title>

      <v-data-table
        :headers="headers2"
        :items="items2"
        :loading="loading"
        :pagination.sync="pagination2"
        :search="search"
        class="px-1 pb-1"
        item-key="name"
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <td class="text-xs-left">
            {{ props.item.entry }}
          </td>

          <td class="text-xs-left">
            {{ props.item.msisdn }}
          </td>

          <td class="text-xs-center">
            {{ props.item.category }}
          </td>

          <td class="text-xs-center">
            {{ props.item.amount }}
          </td>
        </template>

        <template slot="no-data">
          {{ noDataText }}
        </template>

        <template slot="no-result">
          <v-alert
            :value="true"
            color="error"
            icon="warning"
          >
            {{ noResultText }}
          </v-alert>
        </template>

        <template slot="footer">
          <tr>
            <td>&nbsp;</td>
            <td>&nbsp;</td>

            <td class="text-xs-right">
              Total 2nd Incentive:
            </td>

            <td
              class="primary text-xs-center"
            >
              {{ getRm(Number(totalPayDay2)) }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-divider class="primary" />

      <!-- 3rd MNP Incentive -->
      <v-card-title>
        <h3 class="primary--text">
          {{ thirdMnpIncentiveLabel }}
        </h3>

        <v-spacer />
      </v-card-title>

      <v-data-table
        :headers="headers3"
        :items="items3"
        :loading="loading"
        :pagination.sync="pagination3"
        :search="search"
        class="px-1 pb-1"
        item-key="name"
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <td class="text-xs-left">
            {{ props.item.entry }}
          </td>

          <td class="text-xs-left">
            {{ props.item.msisdn }}
          </td>

          <td class="text-xs-center">
            {{ props.item.category }}
          </td>

          <td class="text-xs-center">
            {{ props.item.amount }}
          </td>
        </template>

        <template slot="no-data">
          {{ noDataText }}
        </template>

        <template slot="no-result">
          <v-alert
            :value="true"
            color="error"
            icon="warning"
          >
            {{ noResultText }}
          </v-alert>
        </template>

        <template slot="footer">
          <tr>
            <td>&nbsp;</td>
            <td>&nbsp;</td>

            <td class="text-xs-right">
              Total 3rd Incentive:
            </td>

            <td
              class="primary text-xs-center"
            >
              {{ getRm(Number(totalPayDay3)) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </panel>
</template>

<script>
import { DateTime, Interval } from 'luxon'
import { createGetParams } from '@/rest'
import { getRm } from '@/utils'

const reportTitle = 'MNP Activation Campaign'

export default {
  components: {
    Panel: () => import(/* webpackChunkName: "panel" */ '@/components/Panel'),
  },

  data () {
    return {
      allowedMonths: function (month) {
        const ym = month.split('-')
        const whatMonth = DateTime.local(
          Number(ym[0]),
          Number(ym[1]),
        ).setZone('Asia/Kuala_Lumpur')

        const now = DateTime.local().setZone('Asia/Kuala_Lumpur')

        const fromDate = DateTime.local()
          .setZone('Asia/Kuala_Lumpur')
          .plus({ months: -3 })

        return Interval.fromDateTimes(fromDate, now).contains(whatMonth)
      },

      breadcrumbs: [
        {
          text: 'Campaigns',
          disabled: false,
          to: '/campaigns',
        },
        {
          text: reportTitle,
          disabled: true,
        },
      ],

      expand: true,

      getRm: getRm,

      items: [],
      items2: [],
      items3: [],

      totalPayDay1: 0.00,
      totalPayDay2: 0.00,
      totalPayDay3: 0.00,

      loading: false,

      maxDate: '',
      menu: false,

      month: null,
      monthOf: null,

      noDataText: null,

      noResultText: 'No data that fits that criteria.',

      pagination: {
        descending: true,
        sortBy: 'name',
      },

      pagination2: {
        descending: true,
        sortBy: 'name',
      },

      pagination3: {
        descending: true,
        sortBy: 'name',
      },

      search: '',

      title: reportTitle,

      totalIn: 0,
      totalOut: 0,
    }
  },

  computed: {
    /*
     * New MNP memo:
     *
     * Campaign Period:
     * 1 June 2026 - 30 September 2026
     *
     * Category A:
     * Month 1 = RM15
     * Month 2 = RM0
     * Month 3 = RM5
     *
     * Category B:
     * Month 1 = RM15
     * Month 2 = RM10
     * Month 3 = RM10
     */

    isNewMnpMemoPeriod () {
      if (!this.monthOf) {
        return false
      }

      return this.monthOf >= '2026-06' &&
        this.monthOf <= '2026-09'
    },

    firstMnpIncentiveLabel () {
      if (this.isNewMnpMemoPeriod) {
        return '1st MNP Incentive (RM15)'
      }

      return '1st MNP Incentive (RM10)'
    },

    secondMnpIncentiveLabel () {
      if (this.isNewMnpMemoPeriod) {
        return '2nd MNP Incentive (RM0/10)'
      }

      return '2nd MNP Incentive (RM0/5)'
    },

    thirdMnpIncentiveLabel () {
      if (this.isNewMnpMemoPeriod) {
        return '3rd MNP Incentive (RM5/10)'
      }

      return '3rd MNP Incentive (RM5)'
    },

    headers: function () {
      const headers = []

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Entry',
        value: 'entry',
      })

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Msisdn',
        value: 'msisdn',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Category',
        value: 'category',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Amount',
        value: 'amount',
      })

      return headers
    },

    headers2: function () {
      const headers = []

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Entry',
        value: 'entry',
      })

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Msisdn',
        value: 'msisdn',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Category',
        value: 'category',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Amount',
        value: 'amount',
      })

      return headers
    },

    headers3: function () {
      const headers = []

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Entry',
        value: 'entry',
      })

      headers.push({
        align: 'left',
        sortable: true,
        text: 'Msisdn',
        value: 'msisdn',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Category',
        value: 'category',
      })

      headers.push({
        align: 'center',
        sortable: false,
        text: 'Amount',
        value: 'amount',
      })

      return headers
    },
  },

  watch: {
    monthOf: function (val) {
      this.getBlackActivations(val)
    },
  },

  mounted: function () {
    const now = DateTime.local().setZone('Asia/Kuala_Lumpur')

    this.maxDate = this.month = this.monthOf = now.toFormat('yyyy-LL')
  },

  methods: {
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },

    getBlackActivations (month) {
      const params = createGetParams({
        month: month,
      })

      this.loading = true

      this.items = this.items2 = this.items3 = []
      this.counter = this.counter2 = this.counter3 = []

      this.$rest.get('getMnpCampaign.php', params)
        .then(function (response) {
          if (response.data.items) {
            this.items = response.data.items
          }

          if (response.data.items2) {
            this.items2 = response.data.items2
          }

          if (response.data.items3) {
            this.items3 = response.data.items3
          }

          this.totalPayDay1 = response.data.totalAmount
          this.totalPayDay2 = response.data.totalAmount2
          this.totalPayDay3 = response.data.totalAmount3

          this.noDataText = "There's nothing to show."

          this.loading = false
        }.bind(this))
        .catch(error => {
          this.noDataText = error.message
          this.loading = false
        })
    },
  },
}
</script>
